import { useTranslation } from 'react-i18next'
import { Box, Button, Divider, Grid, Stack, Tooltip, Typography } from '@mui/material'
import { VehicleCard } from './VehicleCard'
import { CountriesOptionsWrapper } from 'core/widgets/CountriesDropdown/CountriesOptionsWrapper'
import { CountriesDropdownType } from 'core/widgets/CountriesDropdown/enum'
import { CountriesDropdownFormik } from '@carsys/form/src/CountriesDropdownFormik'
import { useIsPortal } from 'utils/Subscription/PermissionSelector'
import LicensePlateInputFormikField from '@carsys/form/src/LicensePlateInputFormikField'
import { VinTextField } from '@carsys/form'
import { Alert } from '@carsys/ui'
import { useField, useFormikContext } from 'formik'
import { useIsDuplicateRegistration } from '../../providers/hooks/useIsDuplicateRegistration'
import { useIsDuplicateVIN } from '../../providers/hooks/useIsDuplicateVIN'
import getOr from 'lodash/fp/getOr'
import { UnknownVehicleFormValues } from '../../../UnknownVehicleV2Form'
import { useOpenOrderForVehicle } from 'app/search/hooks/useOpenOrderForVehicle'
import { useHistory } from 'react-router-dom'
import { useVinUnmask } from 'app/vehicles/hooks/use-vin-unmask'
import React, { useState } from 'react'
import { useNotifier } from '@cbgms/base/notifier'
import { EmptyUUID } from '@cbgms/base/utils/uuid'

/**
 * Faking a modal action section in this component.
 * I dont agree, but besides redoing the modal setup for this flow, this is the quickest method.
 * - Dennis Keulen
 * @returns
 */

interface ToolTipProps {
  message: string
  showModal: () => void
  slotBottomButtons?: React.ReactNode
}

const DefaultButtons: React.FC<{
  isDuplicateVIN: boolean
  isDuplicateRegistration: boolean
}> = ({ isDuplicateRegistration, isDuplicateVIN }) => {
  const { t } = useTranslation()
  const { push } = useHistory()
  const openOrderForVehicle = useOpenOrderForVehicle()
  const { isValid, submitForm } = useFormikContext<UnknownVehicleFormValues>()
  const isPortal = useIsPortal()
  const disabled = isDuplicateRegistration || isDuplicateVIN || !isValid
  const viewVehicle = (
    <Button
      sx={{ alignSelf: 'flex-end' }}
      disabled={disabled}
      variant={isPortal ? 'outlined' : 'contained'}
      type={isPortal ? 'button' : 'submit'}
      data-testid='btn-create-vehicle'
      onClick={async e => {
        e.preventDefault()
        const result = (await submitForm()) as any
        push(`/vehicles/${result.vehicle.UUID}`)
      }}
    >
      {t('vehicle.create_vehicle')}
    </Button>
  )

  const addOrder = (
    <Button
      sx={{ alignSelf: 'flex-end' }}
      disabled={disabled}
      variant={isPortal ? 'contained' : 'outlined'}
      type={isPortal ? 'submit' : 'button'}
      data-testid='btn-create-order'
      onClick={async e => {
        e.preventDefault() // stops form submitting twice when hitting enter key
        const result = (await submitForm()) as any
        openOrderForVehicle({ Key: '', Values: [result.vehicle] })
      }}
    >
      {isPortal ? t('order.new_order') : t('workorder.new_workorder')}
    </Button>
  )

  const buttons = isPortal ? [viewVehicle, addOrder] : [addOrder, viewVehicle]

  return (
    <>
      {buttons.map((button, i) => (
        <Grid item key={i}>
          {button}
        </Grid>
      ))}
    </>
  )
}

const ToolTipDuplicate: React.FC<ToolTipProps> = ({ message, showModal }) => {
  const { t } = useTranslation()
  return (
    <Box sx={{ p: 0.5 }}>
      <Box>{message}</Box>
      <Box>
        <Button size='small' onClick={showModal}>
          {t('unknown_vehicle.duplicate_show_vehicle')}
        </Button>
      </Box>
    </Box>
  )
}

export const Confirm: React.FC<{ slotBottomButtons?: React.ReactNode }> = ({ slotBottomButtons }) => {
  const defaultSize = 'small'
  const { t } = useTranslation()
  const isPortal = useIsPortal()

  const [{ value: country }] = useField('Country')
  const [{ value: registrationValue }] = useField('Registration')
  const [{ value: vin }] = useField('Vin')
  const registration = `${country}|${registrationValue}`
  const { isDuplicateRegistration, showDuplicateRegistrationModal } = useIsDuplicateRegistration(country, registrationValue)
  const { isDuplicateVIN, showDuplicateVINModal } = useIsDuplicateVIN(vin)
  const { errors, touched, values: formValues, setValues } = useFormikContext<UnknownVehicleFormValues>()
  const VinOrLicenseplateError = getOr('', 'Vin | Registration', errors)
  const registrationOrVinTouched = !!(touched.Registration || touched.Vin)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const notifier = useNotifier()

  const { unmaskVinByRegistration, isVinMasked, UnmaskConfirmationDialog } = useVinUnmask({
    vin: vin || '',
    vehicleUUID: EmptyUUID,
    registration
  })

  const handleShowVinClick = async () => {
    setConfirmDialogOpen(true)
    return null
  }

  const handleConfirmShowVin = async () => {
    try {
      const unmaskedVin = await unmaskVinByRegistration(registration)
      if (unmaskedVin) {
        setValues({ ...formValues, Vin: unmaskedVin })
        notifier.success(t('Vehicles.VinUnmask.Success'))
      } else {
        notifier.warning(t('Vehicles.VinUnmask.NoVinFound'))
      }
      setConfirmDialogOpen(false)
    } catch (error) {
      console.error('Failed to unmask VIN:', error)
      notifier.error(t('Vehicles.VinUnmask.Error'))
      setConfirmDialogOpen(false)
    }
  }

  return (
    <>
      <Box>
        <Grid container data-testid='create-vehicle-confirm-grid'>
          <Grid item xs={6}>
            <Typography variant='h5'>{t('unknown_vehicle.confirm_title')}</Typography>
            {!isPortal && <Typography variant='body1'>{t('unknown_vehicle.sub_title')}</Typography>}
            <Stack direction='row' gap={1} py={1}>
              <CountriesOptionsWrapper type={CountriesDropdownType.all}>
                {options => (
                  <CountriesDropdownFormik
                    showKeyLabelOption={false}
                    countryOptions={options}
                    name='Country'
                    label={t('vehicle.country')}
                    required={!isPortal}
                    size={defaultSize}
                    style={{ minWidth: '50%' }}
                    textFieldInputProps={{ size: defaultSize }}
                  />
                )}
              </CountriesOptionsWrapper>
              <Tooltip
                title={
                  <ToolTipDuplicate
                    message={t('duplicate_vehicle.title_duplicate_vehicle_registration')}
                    showModal={showDuplicateRegistrationModal}
                  />
                }
                arrow
                placement='right'
                open={isDuplicateRegistration}
                PopperProps={{ style: { zIndex: 1400 } }}
              >
                <div>
                  <LicensePlateInputFormikField
                    country={country}
                    required={!isPortal}
                    name='Registration'
                    data-testid='dynamic-vehicle-summary-registration'
                    size='large'
                    error={isDuplicateRegistration}
                    autoFocus
                  />
                </div>
              </Tooltip>
            </Stack>
            <Stack direction='row' gap={1} py={1}>
              <Tooltip
                title={<ToolTipDuplicate message={t('duplicate_vehicle.title_duplicate_vehicle_vin')} showModal={showDuplicateVINModal} />}
                arrow
                placement='right'
                open={isDuplicateVIN}
                PopperProps={{ style: { zIndex: 1400 } }}
              >
                <div style={{ minWidth: '50%' }}>
                  <VinTextField
                    name='Vin'
                    label={`${t('vehicle.vin')}`}
                    size={defaultSize}
                    fullWidth
                    onShowVinClick={handleShowVinClick}
                    isVinMasked={isVinMasked}
                    registration={registration}
                  />
                </div>
              </Tooltip>
            </Stack>
            {isPortal && (
              <Box p={1} pl={0}>
                <Alert severity='info'>{t('unknown_vehicle.info_provide_registration')}</Alert>
              </Box>
            )}
            {!!VinOrLicenseplateError && registrationOrVinTouched && (
              <Box p={1} pl={0}>
                <Alert severity='error'>{VinOrLicenseplateError}</Alert>
              </Box>
            )}
          </Grid>
          <Grid item xs={6}>
            <VehicleCard />
          </Grid>
        </Grid>
        {isPortal && (
          <Grid container data-testid='create-vehicle-confirm-grid'>
            <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', pt: 2 }}>
              <Divider flexItem orientation='horizontal' sx={{ width: '100%' }} />
              <Grid item container justifyContent='flex-end' spacing={1} pt={2}>
                {slotBottomButtons || <DefaultButtons isDuplicateRegistration={isDuplicateRegistration} isDuplicateVIN={isDuplicateVIN} />}
              </Grid>
            </Grid>
          </Grid>
        )}
      </Box>
      <UnmaskConfirmationDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleConfirmShowVin}
        registration={registration}
        hasVin={!!vin && !isVinMasked(vin)}
      />
    </>
  )
}
